import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import ListenButton from "@/components/ListenButton";

export const AIContentBlock = ({ pageKey, topic, imgQuery, compact = false }) => {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let on = true;
    setLoading(true);
    setError(false);
    api
      .get("/ai-content", { params: { page: pageKey, topic, img_query: imgQuery, lang_code: lang.code, lang_name: lang.name, with_image: !compact }, timeout: 90000 })
      .then((r) => on && setData(r.data))
      .catch(() => on && setError(true))
      .finally(() => on && setLoading(false));
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, lang.code]);

  if (error) return null;

  if (loading) {
    return (
      <section className={`${compact ? "my-4 p-4" : "my-10 p-6 md:p-8"} rounded-3xl border border-purple-100 bg-gradient-to-r from-rose-50/60 via-pink-50/60 to-purple-50/60`} data-testid={`ai-content-loading-${pageKey}`}>
        <div className="flex items-center gap-2 text-purple-600 text-xs font-black uppercase tracking-widest">
          <Sparkles className="w-4 h-4 animate-pulse" /> Curating editorial content{lang.code !== "en" ? ` in ${lang.name}` : ""}...
        </div>
        <div className="mt-4 space-y-3 animate-pulse">
          <div className="h-6 bg-purple-100/70 rounded-full w-2/3" />
          <div className="h-3 bg-purple-100/60 rounded-full w-full" />
          {!compact && <div className="h-3 bg-purple-100/60 rounded-full w-5/6" />}
          <div className="h-3 bg-purple-100/60 rounded-full w-4/6" />
        </div>
      </section>
    );
  }

  if (!data) return null;
  const speech = `${data.title}. ${(data.paragraphs || []).join(" ")}`;
  const showImage = data.image && !compact;

  return (
    <section
      className={`${compact ? "my-4" : "my-10"} rounded-3xl border border-purple-100 bg-gradient-to-r from-rose-50/60 via-pink-50/60 to-purple-50/60 overflow-hidden`}
      data-testid={`ai-content-${pageKey}`}
      dir={lang.rtl ? "rtl" : "ltr"}
    >
      <div className="grid md:grid-cols-5">
        {showImage && (
          <div className="md:col-span-2 relative min-h-[220px]">
            <img src={data.image.url} alt={data.image.alt} className="absolute inset-0 w-full h-full object-cover" />
            <p className="absolute bottom-0 inset-x-0 bg-slate-950/70 backdrop-blur px-4 py-2 text-[11px] text-slate-300" dir="ltr" data-testid={`ai-image-attribution-${pageKey}`}>
              Photo by{" "}
              <a href={data.image.photographer_url} target="_blank" rel="noopener noreferrer" className="font-bold text-white underline decoration-pink-400 hover:text-pink-300">
                {data.image.photographer}
              </a>{" "}
              on{" "}
              <a href={data.image.unsplash_url} target="_blank" rel="noopener noreferrer" className="font-bold text-white underline decoration-pink-400 hover:text-pink-300">
                Unsplash
              </a>
            </p>
          </div>
        )}
        <div className={`${compact ? "p-4 md:p-5" : "p-6 md:p-8"} ${showImage ? "md:col-span-3" : "md:col-span-5"}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-[10px] font-black uppercase tracking-widest">
              <Sparkles className="w-3 h-3" /> AI-curated editorial
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{lang.native}</span>
            <ListenButton text={speech} className="ml-auto" />
          </div>
          <h2 className={`font-display font-black text-slate-900 ${compact ? "text-lg md:text-xl mt-3" : "text-2xl md:text-3xl mt-4"}`} data-testid={`ai-title-${pageKey}`}>{data.title}</h2>
          {(data.paragraphs || []).map((p, i) => (
            <p key={i} className={`${compact ? "text-sm" : "text-sm md:text-base"} text-slate-600 leading-relaxed mt-3`}>{p}</p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AIContentBlock;
